import {
  SubpageHero,
  SubpageLeistungen,
  SubpageProzess,
  SubpageBewertungen,
  SubpageReferenzen,
  SubpageKontakt,
  SubpageFAQ,
  SubpageSEO,
} from "@/components/shared/SubpageComponents";
import { SEOHead } from "@/components/shared/SEOHead";
import {
  ClipboardCheck,
  FileSearch,
  Scale,
  FileBarChart,
  Eye,
  Shield,
} from "lucide-react";

const services = [
  { icon: ClipboardCheck, title: "Gutachten", description: "Fundierte Gutachten für Immobilien und Bauprojekte. Objektiv, detailliert und gerichtsfest." },
  { icon: FileSearch, title: "Bauschadenbewertung", description: "Professionelle Identifikation und Bewertung von Bauschäden und Mängeln." },
  { icon: Scale, title: "Wertermittlung", description: "Marktgerechte Wertermittlung von Immobilien und Grundstücken." },
  { icon: FileBarChart, title: "Beweissicherung", description: "Dokumentation und Beweissicherung bei Bauvorhaben und Streitfällen." },
  { icon: Eye, title: "Generalinspektion Abscheider", description: "Generalinspektion für Fettabscheider, Leichtflüssigkeitsabscheider und Benzinabscheider. Dichtigkeitsnachweise nach DIN EN 1610 (Neubauten) und DIN 1986-30 (Bestandsbauten)." },
  { icon: Shield, title: "Beratung", description: "Fachliche Beratung zu allen Fragen rund um Immobilien, Baumängel und Werterhalt." },
];

const reviews = [
  { quote: "Ich brauchte einen Gutachter für einen Feuchte-Schaden. Herr Freitag ist schnell und pünktlich erschienen. Das Gutachten hatte ich sehr schnell von ihm erhalten. Ich bin sehr zufrieden.", author: "Michael Bertram", company: "Google Bewertung" },
  { quote: "Super nett und hilfsbereit. Schnelle und zuverlässige Abwicklung. Vielen Dank. (Dichtheitsprüfung Abwassertank)", author: "Christian Jäschke", company: "Google Bewertung" },
  { quote: "Die Firma Freitag hat unser gesamtes Bauunternehmen vom Anfang bis zum Ende begleitet. Herr Freitag besitzt über ein profundes Fachwissen und ist auch als Sachverständiger zu empfehlen. Wir würden die Firma Freitag immer wieder beauftragen.", author: "Matthias Lücke", company: "Google Bewertung" },
  { quote: "Lief alles super. Revisionsschacht im Vorgarten wurde preisgünstig realisiert. Immer wieder. Beste Grüße", author: "Fa Schneider", company: "Google Bewertung" },
];

const references = [
  { project: "Gutachten Villa Rheinblick", scope: "Vollgutachten Marktwertermittlung", year: "2024" },
  { project: "Beweissicherung Neubaugebiet", scope: "Dokumentation für 28 Grundstücke", year: "2024" },
  { project: "Schadensgutachten Bürokomplex", scope: "Fassadenschäden & Feuchtigkeit", year: "2023" },
  { project: "Wertermittlung Gewerbepark", scope: "5 Gewerbeeinheiten, 8.500m²", year: "2023" },
  { project: "Bauüberwachung Wohnanlage", scope: "Qualitätssicherung 24 WE", year: "2022" },
  { project: "Gerichtsgutachten Altbau", scope: "Baumängel nach Sanierung", year: "2022" },
];

const faqs = [
  { question: "Was kostet ein Gutachten?", answer: "Die Kosten variieren je nach Art und Umfang des Gutachtens. Kontaktieren Sie uns für ein individuelles Angebot." },
  { question: "Wie lange dauert die Erstellung eines Gutachtens?", answer: "In der Regel dauert die Erstellung eines Gutachtens 2-4 Wochen nach dem Ortstermin." },
  { question: "Sind Ihre Gutachten gerichtsfest?", answer: "Ja, alle unsere Gutachten werden nach den gängigen Standards erstellt und sind gerichtsverwertbar." },
  { question: "Bieten Sie auch telefonische Beratung an?", answer: "Ja, wir bieten eine kostenlose telefonische Erstberatung an, um Ihr Anliegen einzuschätzen." },
];

const seoText = [
  "Das Freitag Sachverständigenbüro ist Ihre unabhängige Anlaufstelle für professionelle Gutachten, Wertermittlungen und Bauschadenbewertungen. Als Teil der Freitag-Gruppe verbinden wir tiefgreifendes Bauwissen mit objektiver Sachverständigenexpertise – für fundierte Entscheidungen bei Immobilienkauf, Baustreitigkeiten oder Qualitätssicherung.",
  "Unser Team aus öffentlich bestellten und vereidigten Sachverständigen erstellt gerichtsfeste Gutachten nach den höchsten fachlichen Standards. Von der Marktwertermittlung über die Beweissicherung bis zur begleitenden Bauüberwachung bieten wir das komplette Leistungsspektrum für private und gewerbliche Auftraggeber.",
  "Vertrauen Sie auf über 20 Jahre Erfahrung im Sachverständigenwesen. Die Freitag-Expertise steht für Objektivität, Gründlichkeit und praxisnahe Lösungen. Vereinbaren Sie noch heute einen unverbindlichen Beratungstermin.",
];

export default function SachverstaendigerPage() {
  return (
    <div>
      <SEOHead
        title="Sachverständiger Braunschweig | Gutachten, Dichtheitsprüfung & Generalinspektion | Freitag-Gruppe"
        description="Sachverständigenbüro Freitag in Braunschweig: Immobiliengutachten, Bauschadenbewertung, Wertermittlung, Dichtheitsprüfung, Generalinspektion Fettabscheider nach DIN EN 1610 & DIN 1986-30. Jetzt beraten lassen!"
        canonical="/sachverstaendiger"
        schemaKey="sachverstaendiger"
      />
      <SubpageHero
        color="orange"
        title="Sachverständiger"
        subtitle="Freitag Sachverständigenbüro"
        description="Unabhängige Expertise für Immobilien und Bauprojekte. Fundierte Gutachten, professionelle Bewertungen und kompetente Beratung."
        image="/images/sv-hero.jpg"
      />
      <SubpageLeistungen color="orange" services={services} />
      <SubpageSEO
        color="orange"
        title="Freitag Sachverständigenbüro – Unabhängige Gutachten & Expertise"
        text={seoText}
      />
      <SubpageReferenzen color="orange" references={references} />
      <SubpageProzess color="orange" />
      <SubpageBewertungen color="orange" reviews={reviews} />
      <SubpageKontakt color="orange" title="Sachverständigen-Anfrage" />
      <SubpageFAQ color="orange" faqs={faqs} />
    </div>
  );
}
